import { t } from '@trimble/i18n';
import {
  AlignmentMeasurementCapability,
  PolylineMeasurementCapability,
  PointMeasurementCapability,
} from '@trimble/web3d-capabilities';
import {
  MeasurementType,
  MeasurementsCapabilityProvider,
} from '@trimble/web3d-providers';
import {
  Component,
  Show,
  Switch,
  Match,
  createMemo,
  Accessor,
  createSignal,
  createEffect,
  on,
  untrack,
} from 'solid-js';
import {
  ControlMenu,
  ControlMenuItemType,
} from '@trimble/ui-components/solidjs';

enum PointMeasurementOption {
  XYCoordinates = 'showXYCoordinates',
  ZCoordinate = 'showZCoordinate',
}

enum PolylineMeasurementOption {
  HorizontalMeasurements = 'showHorizontalMeasurements',
  VerticalMeasurements = 'showVerticalMeasurements',
  SegmentSlopes = 'showSegmentSlopes',
}

export interface SuboptionsDropdownProps {
  type: MeasurementType;
  isOpen: Accessor<boolean>;
  currentType: Accessor<MeasurementType | null>;
  dropdownRef(el: HTMLDivElement): void;
  measurementsProvider: MeasurementsCapabilityProvider;
  polylineMeasurementCapability?: PolylineMeasurementCapability;
  pointMeasurementCapability?: PointMeasurementCapability;
  alignmentMeasurementCapability?: AlignmentMeasurementCapability;
  alignmentOptionsEnabled?: Accessor<boolean>;
  onClose(): void;
}

export const SuboptionsDropdown: Component<SuboptionsDropdownProps> = (
  props
) => {
  const getPointSelectedValue = (): string => {
    const settings = props.measurementsProvider.pointMeasurementSettings();
    if (settings.showXYCoordinates) return PointMeasurementOption.XYCoordinates;
    if (settings.showZCoordinate) return PointMeasurementOption.ZCoordinate;
    return '';
  };

  const [pointValue, setPointValue] = createSignal<string>(
    untrack(() => getPointSelectedValue())
  );

  createEffect(
    on(
      () => props.measurementsProvider.pointMeasurementSettings(),
      () => {
        setPointValue(getPointSelectedValue());
      }
    )
  );

  createEffect(
    on(pointValue, (value, prevValue) => {
      if (prevValue === undefined) return;
      if (value === getPointSelectedValue()) return;

      props.pointMeasurementCapability?.startMeasurement();
      props.onClose();

      if (value === '') {
        props.measurementsProvider.setPointMeasurementSettings({
          showXYCoordinates: false,
          showZCoordinate: false,
        });
        return;
      }

      props.measurementsProvider.setPointMeasurementSettings({
        showXYCoordinates: value === PointMeasurementOption.XYCoordinates,
        showZCoordinate: value === PointMeasurementOption.ZCoordinate,
      });
    })
  );

  const pointOptions = createMemo((): ControlMenuItemType[] => [
    {
      id: 'point-measurement-options',
      type: 'radiogroup',
      value: pointValue,
      setValue: setPointValue,
      isUnselectionAllowed: true,
      options: [
        {
          id: PointMeasurementOption.XYCoordinates,
          label: t('measurements.point.2DXY'),
        },
        {
          id: PointMeasurementOption.ZCoordinate,
          label: t('measurements.point.elevation'),
        },
      ],
    },
  ]);

  const getPolylineSelectedValue = (): string => {
    if (!props.polylineMeasurementCapability) return '';
    const settings =
      props.polylineMeasurementCapability.polylineMeasurementSettings();
    if (settings.showHorizontalMeasurements)
      return PolylineMeasurementOption.HorizontalMeasurements;
    if (settings.showVerticalMeasurements)
      return PolylineMeasurementOption.VerticalMeasurements;
    if (settings.showSegmentSlopes)
      return PolylineMeasurementOption.SegmentSlopes;
    return '';
  };

  const [polylineValue, setPolylineValue] = createSignal<string>(
    untrack(() => getPolylineSelectedValue())
  );

  createEffect(
    on(
      () => props.polylineMeasurementCapability?.polylineMeasurementSettings(),
      () => {
        setPolylineValue(getPolylineSelectedValue());
      }
    )
  );

  createEffect(
    on(polylineValue, (value, prevValue) => {
      if (prevValue === undefined) return;
      if (!props.polylineMeasurementCapability) return;
      if (value === getPolylineSelectedValue()) return;

      props.polylineMeasurementCapability.startMeasurement();
      props.onClose();

      const showSegmentDistances =
        props.polylineMeasurementCapability.polylineMeasurementSettings()
          .showSegmentDistances;

      if (value === '') {
        props.polylineMeasurementCapability.setPolylineMeasurementSettings({
          showSegmentDistances,
          showHorizontalMeasurements: false,
          showVerticalMeasurements: false,
          showSegmentSlopes: false,
        });
        return;
      }

      props.polylineMeasurementCapability.setPolylineMeasurementSettings({
        showSegmentDistances,
        showHorizontalMeasurements:
          value === PolylineMeasurementOption.HorizontalMeasurements,
        showVerticalMeasurements:
          value === PolylineMeasurementOption.VerticalMeasurements,
        showSegmentSlopes: value === PolylineMeasurementOption.SegmentSlopes,
      });
    })
  );

  const polylineOptions = createMemo((): ControlMenuItemType[] => {
    if (!props.polylineMeasurementCapability) return [];
    return [
      {
        id: 'polyline-measurement-options',
        type: 'radiogroup',
        value: polylineValue,
        setValue: setPolylineValue,
        isUnselectionAllowed: true,
        options: [
          {
            id: PolylineMeasurementOption.HorizontalMeasurements,
            label: t('measurements.polyline.2DHorizontal'),
          },
          {
            id: PolylineMeasurementOption.VerticalMeasurements,
            label: t('measurements.polyline.vertical'),
          },
          {
            id: PolylineMeasurementOption.SegmentSlopes,
            label: t('measurements.polyline.slopeAndGrade'),
          },
        ],
      },
    ];
  });

  const alignmentOptions = createMemo((): ControlMenuItemType[] => {
    if (!props.alignmentMeasurementCapability) return [];
    const disabled = !(props.alignmentOptionsEnabled?.() ?? true);
    const tooltip = disabled
      ? t('web3D.toolbar.actions.alignmentMeasurementOptionsUnsupported')
      : undefined;
    return [
      {
        id: 'use-cant',
        type: 'checkbox',
        label: t('web3D.toolbar.actions.alignmentMeasurementUseCant'),
        value: props.alignmentMeasurementCapability.useCant,
        setValue: props.alignmentMeasurementCapability.setUseCant,
        tooltip,
        disabled,
      },
      {
        id: 'use-gradient',
        type: 'checkbox',
        label: t('web3D.toolbar.actions.alignmentMeasurementUseGradient'),
        value: props.alignmentMeasurementCapability.useGradient,
        setValue: props.alignmentMeasurementCapability.setUseGradient,
        tooltip,
        disabled,
      },
    ];
  });

  return (
    <Show when={props.isOpen() && props.currentType() === props.type}>
      <div
        ref={props.dropdownRef}
        class="absolute left-full top-0 ml-2 shadow z-dropdown"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="suboptions-dropdown"
      >
        <Switch>
          <Match when={props.type === 'point'}>
            <ControlMenu items={pointOptions()} />
          </Match>
          <Match when={props.type === 'polyline'}>
            <ControlMenu items={polylineOptions()} />
          </Match>
          <Match when={props.type === 'alignment'}>
            <ControlMenu items={alignmentOptions()} />
          </Match>
        </Switch>
      </div>
    </Show>
  );
};
